import {Instant, LocalDate, ZoneId} from "@js-joda/core";
import {Milestone} from "./FirebaseSchemaModels.js";


export function formatCurrency(amount: number): string {
	  return amount.toLocaleString("en-IN", {style: "currency", currency: "INR"});
}


export function formatDate(millis: number): string {
	  return new Date(millis).toLocaleString("en-GB", {month: "short", day: "2-digit", year: "numeric", weekday: "short"});
}


export function isValidPositiveNumber(value: string | number): boolean {
	  return isFinite(value) && Number(value) > 0;
}


export function getValidPositiveNumber(value: string, minimum = 0): number {
	  return isValidPositiveNumber(value) ? Number(value) : minimum;
}


export const industryKeysNames = Object.freeze({
	  Others: "Others",
	  AccommodationServices: "Accommodation Services",
	  ArtsEntertainmentAndRecreation: "Arts Entertainment And Recreation",
	  Construction: "Construction",
	  EducationalServices: "Educational Services",
	  FinanceAndInsurance: "Finance And Insurance",
	  FoodServices: "Food Services",
	  HealthCareAndSocialAssistance: "Health Care And Social Assistance",
	  Information: "Information",
	  Manufacturing: "Manufacturing",
	  NaturalResourcesAndMining: "Natural Resources And Mining",
	  ProfessionalAndBusinessServices: "Professional And Business Services",
	  RealEstateAndRentalAndLeasing: "Real Estate And Rental And Leasing",
	  Trade: "Trade",
	  TransportationAndWarehousing: "Transportation And Warehousing",
	  Utilities: "Utilities"
});


export function getIndustryKeyFromName(industryName: $Values<typeof industryKeysNames>): string {
	  return Object.keys(industryKeysNames).find(key => industryKeysNames[key] === industryName);
}


export function getIndustryNameFromKey(industryKey: $Keys<typeof industryKeysNames>): string {
	  return industryKeysNames[industryKey];
}


export function getIndustryNamesArray(): string[] {
	  return Object.values(industryKeysNames);
}


export function generateKeyFromName(name: string): string {
	  return name.replace(/\s|\W|_/g, "").toLowerCase();
}


export const views = Object.freeze({
	  products: "Products",
	  milestones: "Milestones",
	  expenses: "Expenses",
	  analytics: "Analytics"
});

export const defView = views.products;

export const proServ = "Product/Service";
export const bus = "Business";
export const pri = "Price";
export const qua = "Quantity";
export const uni = "Unit";

export const cos = "Cost";
export const cat = "Category";


export type MilestoneEscorts = { prev: Milestone, next: Milestone };


export type ExpenseCategory = {
	  serialNumber: number;
	  text: string;
	  description: string;
}

export const expenseCategories: { [key: string]: ExpenseCategory } = Object.freeze({
	  OFFICE_RENT_UTILITIES_MAINTENANCE: {
			 serialNumber: 1,
			 text: "Office Rent, Utilities, and Maintenance",
			 description: "Covers all expenses related to office space, including rent, utilities (electricity, water, internet), and maintenance."
	  },
	  EMPLOYEE_COMPENSATION_BENEFITS: {
			 serialNumber: 2,
			 text: "Employee Compensation and Benefits",
			 description: "Includes salaries, wages, bonuses, and employee benefits like health insurance and retirement plans."
	  },
	  TECHNOLOGY_SOFTWARE_HOSTING: {
			 serialNumber: 3,
			 text: "Technology, Software, and Hosting",
			 description: "Encompasses all expenses for software licenses, hardware, cloud services, cybersecurity, and web hosting."
	  },
	  MARKETING_ADVERTISING: {
			 serialNumber: 4,
			 text: "Marketing and Advertising",
			 description: "Costs associated with digital marketing, social media ads, SEO, public relations, and traditional advertising."
	  },
	  PROFESSIONAL_LEGAL_ACCOUNTING_SERVICES: {
			 serialNumber: 5,
			 text: "Professional, Legal, and Accounting Services",
			 description: "Fees for consultants, legal counsel, accounting services, and other external professional services."
	  },
	  PRODUCT_DEVELOPMENT_RD: {
			 serialNumber: 6,
			 text: "Product Development and R&D",
			 description: "Covers research, development, and production of new products or services, including prototyping and testing."
	  },
	  INSURANCE: {
			 serialNumber: 7,
			 text: "Insurance",
			 description: "Premiums for various insurance policies such as liability, property, and health insurance."
	  },
	  SUPPLIES_MATERIALS: {
			 serialNumber: 8,
			 text: "Supplies and Materials",
			 description: "Costs for office supplies, raw materials, and other essential operational materials."
	  },
	  TRAVEL_ACCOMMODATION: {
			 serialNumber: 9,
			 text: "Travel and Accommodation",
			 description: "Business travel expenses, including flights, hotels, meals, and local transportation."
	  },
	  RECRUITMENT_HIRING_COSTS_TRAINING: {
			 serialNumber: 10,
			 text: "Recruitment, Hiring Costs, Training and Development",
			 description: "Expenses for recruitment, onboarding, and employee training programs, including courses and certifications."
	  },
	  BANKING_TRANSACTION_FEES: {
			 serialNumber: 11,
			 text: "Banking and Transaction Fees",
			 description: "Expenses related to banking services, payment processing fees, and other financial transactions."
	  },
	  OFFICE_EQUIPMENT_FURNITURE: {
			 serialNumber: 12,
			 text: "Office Equipment and Furniture",
			 description: "Purchases of desks, chairs, computers, printers, and other office furnishings and equipment."
	  },
	  INVENTORY_STOCK_MANAGEMENT: {
			 serialNumber: 13,
			 text: "Inventory and Stock Management",
			 description: "Costs for purchasing, storing, and managing inventory or stock necessary for the business."
	  },
	  LICENSES_PERMITS: {
			 serialNumber: 14,
			 text: "Licenses and Permits",
			 description: "Fees for acquiring and renewing business licenses, permits, and certifications."
	  },
	  CUSTOMER_SUPPORT: {
			 serialNumber: 15,
			 text: "Customer Support",
			 description: "Costs for customer service tools, software, and support staff to assist customers."
	  },
	  EVENTS_NETWORKING: {
			 serialNumber: 16,
			 text: "Events and Networking",
			 description: "Expenses for attending or hosting events, conferences, and networking activities."
	  },
	  CORPORATE_SOCIAL_RESPONSIBILITY: {
			 serialNumber: 17,
			 text: "Corporate Social Responsibility (CSR)",
			 description: "Investments in CSR activities, charity donations, sustainability initiatives, and community engagement."
	  },
	  MISCELLANEOUS: {
			 serialNumber: 18,
			 text: "Miscellaneous Expenses",
			 description: "A catch-all category for unexpected or irregular expenses."
	  }
});


export function getExpenseCatTextFromSN(serialNumber: number): string {
	  return Object.values(expenseCategories).find((value) => value.serialNumber === serialNumber).text;
}


export function getExpenseCategorySerialNumberFromText(text: string): number {
	  return Object.values(expenseCategories).find((value) => value.text === text).serialNumber;
}


export function getExpenseCategoryTextArray(): string[] {
	  return Object.values(expenseCategories).map(value => value.text);
}


export function getDateTextFromMillis(millis: number): string {
	  return LocalDate.ofInstant(Instant.ofEpochMilli(millis)).toString();
}


export function getMillisFromDateText(dateText: string): number {
	  return LocalDate.parse(dateText).atStartOfDay().atZone(ZoneId.systemDefault()).toInstant().toEpochMilli();
}
